import { Component, ChangeDetectionStrategy, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SelectModule } from '@openng/optimus-ui/select';

import { EmailValidationResult, RoleOption, TeamUserService } from '../../../core/services/team-user.service';

interface InviteFormData {
  email: string;
  position: string;
  systemRole: number;
}

/** "Invite Employee" dialog including the live email availability indicator. */
@Component({
  selector: 'app-user-invite-modal',
  imports: [FormsModule, ButtonModule, DialogModule, InputTextModule, SelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      header="Invite Employee"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [baseZIndex]="1000000"
      styleClass="w-full max-w-md">
      <form (ngSubmit)="submitted.emit()" class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <label for="email" class="font-semibold">Email <span class="text-red-500">*</span></label>
          <div class="relative">
            <input
              pInputText
              id="email"
              [(ngModel)]="formData().email"
              name="email"
              type="email"
              placeholder="Enter email address"
              [class.ng-invalid]="!!formErrors()['email']"
              [class.ng-dirty]="!!formErrors()['email']"
              [class.border-green-500]="validationClass === 'valid'"
              [class.border-red-500]="validationClass === 'invalid'"
              [class.border-blue-500]="validationClass === 'loading'"
              (input)="onEmailInput($any($event.target).value)"
              class="w-full pr-10" />

            <!-- Validation indicator icon -->
            <div class="absolute right-3 top-1/2 transform -translate-y-1/2">
              @if (validationLoading()) {
                <i class="pi pi-spin pi-spinner text-blue-500"></i>
              }
              @if (validationClass === 'valid') {
                <i class="pi pi-check-circle text-green-500"></i>
              }
              @if (validationClass === 'invalid') {
                <i class="pi pi-times-circle text-red-500"></i>
              }
            </div>
          </div>

          <!-- Validation messages -->
          @if (formErrors()['email']) {
            <small class="p-error">{{ formErrors()['email'] }}</small>
          }
          @if (!formErrors()['email'] && validationMessage) {
            <small
              [class.text-green-600]="validationClass === 'valid'"
              [class.text-red-600]="validationClass === 'invalid'">
              {{ validationMessage }}
            </small>
          }
          @if (!formErrors()['email'] && !validationLoading() && formData().email && !validationResult()) {
            <small class="text-gray-500">Enter email to check availability</small>
          }
        </div>

        <!-- Full Name is not required for invite - user will provide it when accepting invite -->

        <div class="flex flex-col gap-2">
          <label for="position" class="font-semibold">Position</label>
          <input
            pInputText
            id="position"
            [(ngModel)]="formData().position"
            name="position"
            placeholder="Enter position"
            class="w-full" />
        </div>

        <div class="flex flex-col gap-2">
          <label for="systemRole" class="font-semibold">Role <span class="text-red-500">*</span></label>
          <p-select
            [options]="roleOptions()"
            [(ngModel)]="formData().systemRole"
            name="systemRole"
            placeholder="Select a role"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full"
            [appendTo]="'body'">
          </p-select>
          @if (formErrors()['systemRole']) {
            <small class="p-error">{{ formErrors()['systemRole'] }}</small>
          }
        </div>

        <div class="flex justify-end gap-2 pt-4">
          <p-button label="Cancel" severity="secondary" (onClick)="cancelled.emit()" type="button"></p-button>
          <p-button label="Send Invite" [loading]="loading()" type="submit" icon="pi pi-send"></p-button>
        </div>
      </form>
    </p-dialog>
  `
})
export class UserInviteModalComponent {
  private readonly teamUserService = inject(TeamUserService);

  readonly visible = model(false);
  readonly formData = input.required<InviteFormData>();
  readonly roleOptions = input<RoleOption[]>([]);
  readonly formErrors = input<Record<string, string>>({});
  readonly loading = input(false);

  readonly emailChange = output<string>();
  readonly submitted = output<void>();
  readonly cancelled = output<void>();

  readonly validationResult = signal<EmailValidationResult | null>(null);
  readonly validationLoading = signal(false);

  // Debounced live availability check, previously owned by the container.
  private readonly emailValidationSubject = new Subject<string>();

  // Reopening the dialog starts without stale validation state.
  private readonly resetOnClose = effect(() => {
    if (!this.visible()) {
      this.validationResult.set(null);
      this.validationLoading.set(false);
    }
  });

  constructor() {
    this.emailValidationSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(email => {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.validateEmail(email);
      } else {
        this.validationResult.set(null);
      }
    });
  }

  onEmailInput(email: string): void {
    this.formData().email = email;
    this.emailChange.emit(email);

    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.emailValidationSubject.next(email);
    } else {
      this.validationResult.set(null);
    }
  }

  private validateEmail(email: string): void {
    this.validationLoading.set(true);
    this.teamUserService.validateEmailForInvitation(email).subscribe({
      next: (result) => {
        this.validationResult.set(result);
        this.validationLoading.set(false);
      },
      error: () => {
        this.validationResult.set({
          isValid: false,
          canInvite: false,
          message: 'Failed to validate email'
        });
        this.validationLoading.set(false);
      }
    });
  }

  get validationClass(): string {
    const email = this.formData().email;
    const result = this.validationResult();
    if (!email || !result) return '';
    if (this.validationLoading()) return 'loading';
    return result.canInvite ? 'valid' : 'invalid';
  }

  get validationMessage(): string {
    return this.validationResult()?.message ?? '';
  }
}
